const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const safeNumber = (value: number | null | undefined, fallback = 0) =>
  Number.isFinite(value as number) ? Number(value) : fallback

const buildPreviousValue = (currentValue: number, pctChange: number) => {
  const ratio = 1 + pctChange / 100
  if (ratio <= 0) return Math.max(currentValue * 0.6, 1)
  return currentValue / ratio
}

const normalizeSeries = (values: number[], min = 2, max = 96) => {
  if (!values.length) return []
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)

  if (Math.abs(maxValue - minValue) < 1e-6) {
    return values.map(() => (min + max) / 2)
  }

  return values.map((value) => clamp(((value - minValue) / (maxValue - minValue)) * (max - min) + min, min, max))
}

const buildTrendSeries = (
  currentValue: number,
  pctChange: number,
  length: number,
  volatility = 0.08,
) => {
  const current = Math.max(1, safeNumber(currentValue, 1))
  const previous = buildPreviousValue(current, safeNumber(pctChange))
  const span = Math.max(current, previous)
  const series = Array.from({ length }, (_, index) => {
    const t = index / Math.max(1, length - 1)
    const baseline = previous + (current - previous) * t
    const swingWeight = 1 - Math.abs(0.5 - t) * 1.5
    const wave = Math.sin(t * Math.PI * 2.4 + Math.PI * 0.25) * span * volatility * Math.max(0, swingWeight)
    return Math.max(0, baseline + wave)
  })

  series[length - 1] = current
  return series
}

export const buildProjectsAreaSeries = (
  totalProjects?: number,
  projectsPctChange?: number,
): number[] => {
  const raw = buildTrendSeries(
    safeNumber(totalProjects, 1200),
    safeNumber(projectsPctChange, 6),
    10,
    0.06,
  )

  return normalizeSeries(raw, 4, 92).map((value) => Math.round(value))
}

export const buildRoundsGaugeProgress = (
  totalFundingRounds?: number,
  roundsAdded30d?: number | null,
  roundsPctChange?: number,
) => {
  const totalRounds = Math.max(1, safeNumber(totalFundingRounds, 1))
  const addedRatio = Math.abs(safeNumber(roundsAdded30d)) / totalRounds
  const pctRatio = Math.abs(safeNumber(roundsPctChange, NaN)) / 100
  const hasPct = Number.isFinite(pctRatio)

  const raw = hasPct ? pctRatio : addedRatio

  if (raw <= 0) return 0

  return clamp(raw, 0.02, 0.22)
}

export const buildFundingBarSeries = (
  averageFundingRoundSize?: number,
  totalFundingRounds?: number,
): number[] => {
  const base = Math.max(1, safeNumber(averageFundingRoundSize, 1_000_000))
  const rounds = Math.max(1, safeNumber(totalFundingRounds, 20))
  const roundsFactor = clamp(Math.log10(rounds + 1) / 2.2, 0.35, 1.1)
  const raw = Array.from({ length: 15 }, (_, index) => {
    const phase = index / 14
    const waveA = Math.sin(index * 0.58 + roundsFactor * 1.6)
    const waveB = Math.cos(index * 0.31 + 0.8)
    const trend = 0.92 + phase * 0.22
    return base * trend * (1 + waveA * 0.16 + waveB * 0.1)
  })

  return normalizeSeries(raw, 12, 92).map((value) => Math.round(value))
}

export const buildInvestorsLineSeries = (
  totalInvestors?: number,
  investorsAdded30d?: number | null,
  investorsPctChange?: number,
): number[] => {
  const current = Math.max(1, safeNumber(totalInvestors, 2000))
  const pct = safeNumber(investorsPctChange, 6)
  const added = safeNumber(investorsAdded30d)
  const resolvedPct = added !== 0 ? (added / Math.max(current - added, 1)) * 100 : pct

  const trendSeries = buildTrendSeries(current, resolvedPct, 11, 0.12)
  const adjusted = trendSeries.map((value, index) => {
    const accent = Math.sin(index * 1.4 + 0.5) * current * 0.035
    return Math.max(0, value + accent)
  })

  return normalizeSeries(adjusted, 16, 92).map((value) => Math.round(value))
}

export const buildAverageValue = (data: number[]) => {
  if (!data.length) return 50
  return data.reduce((sum, value) => sum + value, 0) / data.length
}
